import {
    Alert,
    Box,
    Button,
    Container,
    CopyToClipboard,
    ExpandableSection,
    Header,
    SpaceBetween,
    Spinner,
    StatusIndicator
} from "@amzn/awsui-components-react";
import * as React from "react";
import { KBMcpToolOutput } from "../../api/knowledge-base.api";
import { useAPI2 } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";
import { ErrorFlashbar } from "../common/ErrorFlashbar";

/**
 * "MCP server" section: one click creates this instance's search tool on the shared
 * Knowledge Base MCP Amazon Bedrock AgentCore Gateway, then shows the four values an Amazon
 * Quick "Service authentication" connector needs - all server-provided, nothing to register
 * anywhere. Trust model: possession of these credentials is the authorization (same as the
 * AI Teammate connect flow above); the admin consents to that exposure by creating the tool.
 */
export const KBMcpSection = (props: {
    instanceId: string;
}) => {
    const context = React.useContext(AppContext);
    const api = context.getKnowledgeBaseAPI();
    const getRunner = useAPI2((instanceId: string) => api.getKBMcpTool(instanceId));
    const createRunner = useAPI2((instanceId: string) => api.createKBMcpTool(instanceId));
    const deleteRunner = useAPI2((instanceId: string) => api.deleteKBMcpTool(instanceId));

    const [tool, setTool] = React.useState<KBMcpToolOutput>(null);

    React.useEffect(() => {
        // Clear first: the loading spinner only shows while `tool` is null, so without this a
        // switch between instances renders the PREVIOUS instance's status until the new load
        // lands - e.g. READY for an instance that has no tool.
        setTool(null);
        getRunner.invoke(props.instanceId);
    }, [props.instanceId]);

    /**
     * useAPI2 neither cancels nor correlates requests: a response that was in flight when the
     * instance changed still resolves and lands in `output`. Every response carries the
     * instanceId it was computed for, so drop anything that is not the instance on screen -
     * otherwise one instance's tab can show another's tool status.
     */
    const applyIfCurrent = (output: KBMcpToolOutput) => {
        if (output?.instanceId === props.instanceId) setTool(output);
    };

    React.useEffect(() => {
        if (getRunner.status === "Succeeded") applyIfCurrent(getRunner.output);
    }, [getRunner.status, getRunner.output, props.instanceId]);

    React.useEffect(() => {
        if (createRunner.status === "Succeeded") applyIfCurrent(createRunner.output);
    }, [createRunner.status, createRunner.output, props.instanceId]);

    React.useEffect(() => {
        if (deleteRunner.status === "Succeeded") applyIfCurrent(deleteRunner.output);
    }, [deleteRunner.status, deleteRunner.output, props.instanceId]);

    // A fresh gateway target provisions for ~30s (status CREATING). Poll until it settles so
    // the credentials appear the moment the tool is actually callable - without this, an admin
    // could paste values into Quick before the tool is discoverable. Keyed on the runner
    // status too: a failed poll leaves `tool` unchanged, and without the extra key one
    // transient error would end the loop for good.
    React.useEffect(() => {
        if (tool?.status !== "CREATING" || getRunner.status === "Running") return;
        const timer = setTimeout(() => getRunner.invoke(props.instanceId), 5000);
        return () => clearTimeout(timer);
    }, [tool, getRunner.status, props.instanceId]);

    /** `display` shows friendlier text (e.g. a masked secret) while copying the raw value. */
    const copyRow = (label: string, value: string, display?: string) => (
        <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">{label}</Box>
            <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
                {display && <Box>{display}</Box>}
                <CopyToClipboard
                    variant={display ? "icon" : "inline"}
                    textToCopy={value}
                    copyButtonAriaLabel={`Copy ${label}`}
                    copySuccessText={`${label} copied`}
                    copyErrorText={`${label} failed to copy`}
                />
            </SpaceBetween>
        </SpaceBetween>
    );

    const getContent = () => {
        if (!tool) return null;
        if (tool.status === "GATEWAY_MISSING") {
            return <Alert type="warning">
                The Knowledge Base MCP gateway is not deployed on this stage yet. Ask the
                PathFinder team (#pathfinder) to deploy the agentcore-kb stack.
            </Alert>;
        }
        if (tool.status === "NOT_CREATED") {
            return <SpaceBetween size="m">
                <div>
                    Create an MCP search tool to query this instance's Knowledge Base directly
                    from Amazon Quick - judge retrieval quality, and give oncall a way to
                    reproduce search-quality issues against this exact Knowledge Base. Creating
                    it also generates the connection values Quick needs - no other registration
                    anywhere.
                </div>
                <Alert type="warning">
                    This is a diagnostic tool. The connection credentials are shared
                    across ALL Knowledge Base MCP tools on this PathFinder environment - anyone
                    holding them can search every instance's Knowledge Base that has an MCP tool,
                    regardless of page permissions. Treat them like a password and share them
                    only with people who may read the content (same exposure as connecting an
                    AI Teammate above).
                </Alert>
                <Button
                    variant="primary"
                    loading={createRunner.status === "Running"}
                    disabled={deleteRunner.status === "Running"}
                    onClick={() => createRunner.invoke(props.instanceId)}
                >
                    Create MCP tool
                </Button>
            </SpaceBetween>;
        }
        if (tool.status === "CREATING") {
            return <SpaceBetween size="m">
                <StatusIndicator type="in-progress">
                    Provisioning the MCP tool{tool.targetStatus ? ` (${tool.targetStatus})` : ""} -
                    usually ready in under a minute. The connection values will appear here when
                    it's callable.
                </StatusIndicator>
            </SpaceBetween>;
        }
        return <SpaceBetween size="m">
            <StatusIndicator type="success">
                Tool ready
            </StatusIndicator>
            <Box variant="p" color="text-body-secondary">
                In Amazon Quick: Connectors → <b>Create for your team</b> → <b>MCP</b> (this is a
                cloud connector - skip anything about local commands or executables), then paste
                the four values below with Authentication method = <b>Service authentication</b>.
                Publish, and everyone on the team can use it - no sign-in popups. Treat the
                Client Secret like a password.
            </Box>
            {tool.gatewayUrl && copyRow("MCP server endpoint", tool.gatewayUrl)}
            {tool.clientId && copyRow("Client ID", tool.clientId)}
            {tool.clientSecret && copyRow("Client Secret", tool.clientSecret, "••••••••••••")}
            {tool.tokenUrl && copyRow("Token URL", tool.tokenUrl)}
            <ExpandableSection headerText="After connecting">
                <SpaceBetween size="s">
                    <Box variant="p">
                        Verify the tool below appears in Quick's review step, then just ask Quick a
                        question about this instance's content - it will call the tool
                        automatically.
                    </Box>
                    {copyRow("Tool name", tool.toolName)}
                </SpaceBetween>
            </ExpandableSection>
            <ExpandableSection headerText="Reporting a search-quality issue to the PathFinder team">
                <Box variant="p">
                    Include the <b>MCP server endpoint</b> and <b>Tool name</b> (plus the query
                    that misbehaved) in your ticket so the on-call can reproduce against your
                    Knowledge Base directly. <b>Never paste the Client Secret into a ticket</b> -
                    the on-call can retrieve it themselves.
                </Box>
            </ExpandableSection>
            <Button
                loading={deleteRunner.status === "Running"}
                disabled={createRunner.status === "Running"}
                onClick={() => deleteRunner.invoke(props.instanceId)}
            >
                Delete MCP tool
            </Button>
        </SpaceBetween>;
    };

    return (
        <SpaceBetween size="m">
            {getRunner.status === "Error" && <ErrorFlashbar error={getRunner.err} />}
            {createRunner.status === "Error" && <ErrorFlashbar error={createRunner.err} />}
            {deleteRunner.status === "Error" && <ErrorFlashbar error={deleteRunner.err} />}
            <Container header={
                <Header
                    variant="h3"
                    description="Query this instance's Knowledge Base directly from Amazon Quick - judge retrieval quality, and give oncall a way to reproduce search-quality issues against this exact Knowledge Base."
                >
                    MCP server
                </Header>}>
                {getRunner.status === "Running" && !tool ?
                    <Box textAlign="center" className="awsui-util-status-inactive">
                        <Spinner /> Loading MCP tool
                    </Box> :
                    getContent()
                }
            </Container>
        </SpaceBetween>
    );
};
